import styled from 'styled-components'
import { SkeletonRows } from 'components/Skeleton'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { OverdueExplainer } from './Explainers'
import LoadError from './LoadError'
import { dueInfo, formatDay } from './remedialDates'
import { severityMeta } from './remedialMeta'
import { BORDER, INK, MUTED, TextButton } from './ui'

const DashboardSection = ({ section, state, onOpenView, onOpenRemedial, onRetry, showBuilding }) => {
  const items = state?.items || []
  const total = state?.total || 0
  const loading = !state?.loaded
  const failed = !loading && !!state?.error
  return (
    <Panel
      aria-labelledby={`remedial-section-${section.key}`}
      data-tour-id={section.key === 'overdue' ? 'remedials.dashboard-overdue' : undefined}
    >
      <Head>
        <Title id={`remedial-section-${section.key}`}>{section.title}</Title>
        {section.key !== 'critical_no_task' && <OverdueExplainer />}
        {!loading && !failed && <Total>{total}</Total>}
      </Head>
      {loading ? (
        <SkeletonRows rows={3} widths={['12%', '55%', '18%']} />
      ) : failed ? (
        <LoadError onRetry={onRetry ? () => onRetry(section.key) : undefined} />
      ) : items.length === 0 ? (
        <Empty>{section.empty}</Empty>
      ) : (
        <List>
          {items.map(action => {
            const sev = severityMeta(action.severity)
            const due = dueInfo(action)
            const tone = due.level ? RAG[due.level] : null
            return (
              <Row key={action.id} type='button' onClick={() => onOpenRemedial(action)}>
                <Pill style={{ color: sev.color, background: sev.bg }}>{sev.label}</Pill>
                <Main>
                  <RowTitle>{action.title}</RowTitle>
                  {showBuilding && action.buildingPassport?.name && <Sub>{action.buildingPassport.name}</Sub>}
                </Main>
                <Due>
                  {due.label
                    ? <DueTag style={tone ? { color: tone.fg, background: tone.bg } : undefined}>{due.label}</DueTag>
                    : <Sub>{due.date ? formatDay(due.date) : 'No due date'}</Sub>}
                </Due>
              </Row>
            )
          })}
        </List>
      )}
      {!loading && !failed && total > 0 && (
        <TextButton
          type='button'
          onClick={() => onOpenView(section.seeAll)}
          data-tour-id={section.key === 'overdue' ? 'remedials.dashboard-overdue-seeall' : undefined}
        >
          See all {total}
        </TextButton>
      )}
    </Panel>
  )
}

export default DashboardSection

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Title = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
`

const Total = styled.span`
  margin-left: auto;
  font-size: 15px;
  font-weight: 700;
  color: ${MUTED};
`

const Empty = styled.p`
  margin: 4px 0;
  font-size: 15px;
  color: ${MUTED};
`

const List = styled.div`
  display: flex;
  flex-direction: column;
`

const Row = styled.button`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 8px;
  border: none;
  border-top: 1px solid #f4f4f5;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  &:hover { background: #fafafa; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: -2px; }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`

const Pill = styled.span`
  justify-self: start;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
`

const Main = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const RowTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const Sub = styled.span`
  font-size: 14px;
  color: ${MUTED};
`

const Due = styled.span`
  justify-self: end;
  @media (max-width: 768px) { justify-self: start; }
`

const DueTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  color: ${MUTED};
`
